import { FiLock } from 'react-icons/fi';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import {
  useGetPlanLimitsQuery,
  useGetCurrentTenantQuery,
  useGetOrgProductsQuery,
  useGetMyProductLicensesQuery,
} from '@/store/api/apiSlice';
import ProjectSelector from './ProjectSelector';
import CoTaskrLogo from '@/components/auth/CoTaskrLogo';
import SidebarBrandMark from './SidebarBrandMark';
import SidebarToggle from './SidebarToggle';
import {
  SIDEBAR_COLLAPSED_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
  clampSidebarWidth,
} from '@/utils/sidebarLayout';
import {
  isPlatformOperator,
  platformAdminPath,
  parsePlatformAdminTab,
  type PlatformAdminTab,
} from '@/utils/platformOperator';
import {
  isOrgAdminRole,
  ORG_HOME_PATH,
  ORG_PRODUCTS_PATH,
  ORG_SECURITY_PATH,
  shouldUseOrgConsoleNav,
} from '@/utils/orgConsole';
import { parseTenantProducts } from '@/utils/productAccess';
import type { ProductId } from '@/config/products';
import { userHasProductLicense } from '@/utils/productLicense';

interface NavItem {
  name: string;
  path: string;
  iconContent: React.ReactNode;
  badge?: string;
  badgeClass?: string;
  tourId?: string;
  /** When true, item only shows if tenant economics is enabled. */
  requiresEconomics?: boolean;
  /** Roles that can see this item. undefined = visible to all. */
  visibleTo?: UserRole[];
  /** Subscription feature key required. Item shows as locked if not on plan. */
  requiredFeature?: string;
  /** Computed: true when requiredFeature is not available on current plan. */
  isLocked?: boolean;
  /** When true, NavLink only highlights on exact path match (no prefix). */
  end?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
  /** Roles that can see the entire group. undefined = visible to all. */
  visibleTo?: UserRole[];
}

const I = ({ children }: { children: React.ReactNode }) => (
  <svg
    className="w-[18px] h-[18px] shrink-0 stroke-current fill-none"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 18 18"
  >
    {children}
  </svg>
);

const SIDEBAR_GROUPS_STORAGE_KEY = 'co_taskr_sidebar_groups_v2';

const PLATFORM_NAV: { tab: PlatformAdminTab | 'roles'; label: string; iconContent: React.ReactNode }[] = [
  {
    tab: 'organizations',
    label: 'Organizations',
    iconContent: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M9 4V2M5 8h8M5 11h5" />
      </>
    ),
  },
  {
    tab: 'consumption',
    label: 'Consumption',
    iconContent: (
      <>
        <rect x="2" y="12" width="3" height="4" rx="0.5" />
        <rect x="6.5" y="8" width="3" height="8" rx="0.5" />
        <rect x="11" y="4" width="3" height="12" rx="0.5" />
      </>
    ),
  },
  {
    tab: 'audit',
    label: 'Platform audit',
    iconContent: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="1.5" />
        <path d="M5 7h8M5 10h5M5 13h6" />
      </>
    ),
  },
  { tab: 'waitlist', label: 'Waitlist', iconContent: <path d="M2 5h14M2 9h10M2 13h6" /> },
  { tab: 'feedback', label: 'Feedback', iconContent: <path d="M3 4h12v8H7l-3 3V4z" /> },
  {
    tab: 'catalog',
    label: 'Catalog & pricing',
    iconContent: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="2" />
        <path d="M5 7h8M5 10h5" />
      </>
    ),
  },
  {
    tab: 'entitlements',
    label: 'Products & entitlements',
    iconContent: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M5 8h8M5 11h5M9 14h4" />
      </>
    ),
  },
  {
    tab: 'roles',
    label: 'System roles',
    iconContent: (
      <>
        <path d="M12 2L2 7v7c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7L12 2z" />
        <path d="M9 12l2 2 4-4" />
      </>
    ),
  },
];

/** Org admin console — organization administration only (no Plan delivery menus). */
const ORG_CONSOLE_NAV: { path: string; label: string; iconContent: React.ReactNode }[] = [
  {
    path: ORG_HOME_PATH,
    label: 'Workspace home',
    iconContent: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="1.5" />
        <path d="M5 7h8M5 10h5M5 13h3" />
      </>
    ),
  },
  {
    path: ORG_PRODUCTS_PATH,
    label: 'Products & seats',
    iconContent: (
      <>
        <rect x="2" y="4" width="14" height="10" rx="1.5" />
        <path d="M5 8h8M5 11h5" />
      </>
    ),
  },
  {
    path: '/org/licenses',
    label: 'Licenses & users',
    iconContent: (
      <>
        <circle cx="6" cy="7" r="3" />
        <path d="M2 17c0-2.5 1.8-4 4-4s4 1.5 4 4" />
        <path d="M13 8h4M13 11h3M13 14h2" />
      </>
    ),
  },
  {
    path: '/members',
    label: 'Org Members',
    iconContent: (
      <>
        <circle cx="7" cy="7" r="3.5" />
        <circle cx="13" cy="7" r="3.5" />
        <path d="M1 17c0-3 2.2-5 5-5M7 12c2.2 0 4.5 1.5 5 4" />
      </>
    ),
  },
  {
    path: '/billing',
    label: 'Billing & Plan',
    iconContent: (
      <>
        <rect x="1" y="4" width="16" height="11" rx="2" />
        <path d="M1 8h16" />
      </>
    ),
  },
  {
    path: '/admin/org-settings',
    label: 'Plan settings',
    iconContent: (
      <>
        <circle cx="9" cy="9" r="3" />
        <path d="M9 1v2M9 15v2M1 9h2M15 9h2" />
      </>
    ),
  },
  {
    path: ORG_SECURITY_PATH,
    label: 'Security',
    iconContent: (
      <>
        <rect x="3" y="8" width="12" height="8" rx="1.5" />
        <path d="M6 8V6a3 3 0 016 0v2" />
      </>
    ),
  },
  {
    path: '/webhooks',
    label: 'Webhooks',
    iconContent: (
      <>
        <path d="M3 9h12M3 13h12M7 5l-4 4 4 4M11 5l4 4-4 4" />
      </>
    ),
  },
  {
    path: '/workflows',
    label: 'Workflows',
    iconContent: (
      <>
        <path d="M2 6h14M2 10h10M6 14h8" />
      </>
    ),
  },
  {
    path: '/admin/audit-logs',
    label: 'Audit Logs',
    iconContent: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="1.5" />
        <path d="M5 7h8M5 10h5M5 13h6" />
      </>
    ),
  },
  {
    path: '/integrations',
    label: 'Integrations',
    iconContent: (
      <>
        <circle cx="5" cy="5" r="2.5" />
        <circle cx="13" cy="5" r="2.5" />
        <circle cx="9" cy="13" r="2.5" />
        <path d="M7 5h4M6.5 7l1.5 4M11.5 7l-1.5 4" />
      </>
    ),
  },
  {
    path: '/sso-config',
    label: 'SSO',
    iconContent: (
      <>
        <rect x="3" y="8" width="12" height="8" rx="1.5" />
        <path d="M6 8V6a3 3 0 016 0v2" />
      </>
    ),
  },
];

const PLATFORM_MY_ORG: { path: string; label: string; iconContent: React.ReactNode }[] = [
  {
    path: '/admin/org-settings',
    label: 'Org settings',
    iconContent: (
      <>
        <circle cx="9" cy="9" r="3" />
        <path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.6 3.6l1.4 1.4M13 13l1.4 1.4M3.6 14.4l1.4-1.4M13 5l1.4-1.4" />
      </>
    ),
  },
  {
    path: '/members',
    label: 'Org members',
    iconContent: (
      <>
        <circle cx="7" cy="7" r="3.5" />
        <circle cx="13" cy="7" r="3.5" />
        <path d="M1 17c0-3 2.2-5 5-5M7 12c2.2 0 4.5 1.5 5 4" />
      </>
    ),
  },
  {
    path: '/billing',
    label: 'Billing & plan',
    iconContent: (
      <>
        <rect x="1" y="4" width="16" height="11" rx="2" />
        <path d="M1 8h16" />
      </>
    ),
  },
  {
    path: '/integrations',
    label: 'Integrations',
    iconContent: (
      <>
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </>
    ),
  },
  {
    path: '/webhooks',
    label: 'Webhooks',
    iconContent: (
      <>
        <path d="M3 9h12M3 13h12M7 5l-4 4 4 4M11 5l4 4-4 4" />
      </>
    ),
  },
  {
    path: '/workflows',
    label: 'Workflows',
    iconContent: (
      <>
        <path d="M4 9h5l2-3 2 6 2-3h3" />
        <circle cx="4" cy="9" r="1.5" />
        <circle cx="16" cy="9" r="1.5" />
      </>
    ),
  },
  {
    path: '/admin/audit-logs',
    label: 'Tenant audit logs',
    iconContent: (
      <>
        <rect x="2" y="3" width="14" height="12" rx="1.5" />
        <path d="M5 7h8M5 10h5M5 13h6" />
      </>
    ),
  },
];

const PLATFORM_PERSONAL: { path: string; label: string; iconContent: React.ReactNode }[] = [
  {
    path: '/settings',
    label: 'Account settings',
    iconContent: (
      <>
        <circle cx="9" cy="6" r="2.5" />
        <path d="M3 16c0-2.5 2.5-4 6-4s6 1.5 6 4" />
      </>
    ),
  },
  {
    path: '/sso-config',
    label: 'SSO configuration',
    iconContent: (
      <>
        <rect x="3" y="5" width="12" height="10" rx="1.5" />
        <path d="M6 9h6M6 12h4" />
      </>
    ),
  },
];

const NAV_LABEL_KEYS: Record<string, string> = {
  '/dashboard': 'nav.dashboard',
  '/projects': 'nav.projects',
  '/board': 'nav.board',
  '/backlog': 'nav.backlog',
  '/list': 'nav.listView',
  '/sprints': 'nav.sprints',
  '/milestones': 'nav.milestones',
  '/gantt': 'nav.schedule',
  '/time-tracking': 'nav.timesheet',
  '/calendar': 'nav.calendar',
  '/workload': 'nav.teamCapacity',
  '/handbook': 'nav.handbook',
  '/ai': 'nav.ai',
  '/ai-studio': 'nav.aiStudio',
  '/ai-whiteboard': 'nav.whiteboard',
  '/ai-assistant': 'nav.aiAssistant',
  '/reports': 'nav.reports',
  '/stakeholder': 'nav.executiveSummary',
  '/economics': 'nav.projectEconomics',
  '/cost': 'nav.cost',
  '/sso-config': 'nav.ssoConfig',
  '/admin/org-settings': 'nav.orgSettings',
  '/admin/audit-logs': 'nav.auditLogs',
  '/members': 'nav.members',
  '/billing': 'nav.billingPlan',
  '/integrations': 'nav.integrations',
  '/webhooks': 'nav.webhooks',
  '/workflows': 'nav.workflows',
  '/roles': 'nav.rolesPermissions',
  '/admin': 'nav.orgManagement',
  '/ai-usage': 'nav.aiUsage',
};

const GROUP_LABEL_KEYS: Record<string, string> = {
  HOME: 'nav.groups.HOME',
  WORK: 'nav.groups.WORK',
  TIME: 'nav.groups.TIME',
  DOCS: 'nav.groups.DOCS',
  AI: 'nav.groups.AI',
  INSIGHTS: 'nav.groups.INSIGHTS',
  ADMIN: 'nav.groups.ADMIN',
  PLATFORM: 'nav.groups.PLATFORM',
  TOOLS: 'nav.groups.TOOLS',
  MY_ORG: 'nav.groups.MY_ORG',
};

type SidebarProps = {
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  width: number;
  onWidthChange: (width: number) => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

export default function Sidebar({
  isCollapsed,
  setIsCollapsed,
  width,
  onWidthChange,
  mobileOpen = false,
}: SidebarProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const platformMode = isPlatformOperator(user?.role);
  const orgConsoleMode = shouldUseOrgConsoleNav(user?.role, location.pathname);
  const activePlatformTab = parsePlatformAdminTab(location.search);
  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      if (isCollapsed) return;
      e.preventDefault();
      isResizingRef.current = true;
      setIsResizing(true);
      const startX = e.clientX;
      const startWidth = width;

      const onMove = (ev: MouseEvent) => {
        if (!isResizingRef.current) return;
        const next = clampSidebarWidth(startWidth + (ev.clientX - startX));
        onWidthChange(next);
      };

      const onUp = () => {
        isResizingRef.current = false;
        setIsResizing(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [isCollapsed, width, onWidthChange],
  );

  const sidebarWidthPx = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : clampSidebarWidth(width);
  /** Collapsed unless listed here — ADMIN stays open so org admins find settings. */
  const DEFAULT_COLLAPSED_GROUPS = ['TIME', 'AI', 'INSIGHTS', 'PLATFORM'];
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_GROUPS_STORAGE_KEY);
      if (!saved) return new Set(DEFAULT_COLLAPSED_GROUPS);
      const parsed = new Set(JSON.parse(saved) as string[]);
      const legacyMap: Record<string, string> = {
        PLANNING: 'WORK',
        TRACKING: 'TIME',
        SCHEDULE: 'TIME',
        MANAGE: 'ADMIN',
        WORKSPACE: 'HOME',
        TEAM: 'ADMIN',
      };
      let changed = false;
      const migrated = [...parsed].map((g) => {
        const next = legacyMap[g] ?? g;
        if (next !== g) changed = true;
        return next;
      });
      // Expand ADMIN by default (was collapsed in older builds).
      if (migrated.includes('ADMIN')) {
        migrated.splice(migrated.indexOf('ADMIN'), 1);
        changed = true;
      }
      const nextSet = new Set(migrated);
      if (changed) {
        try {
          localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify(migrated));
        } catch {
          /* ignore */
        }
      }
      return nextSet;
    } catch {
      return new Set(DEFAULT_COLLAPSED_GROUPS);
    }
  });
  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      try {
        localStorage.setItem(SIDEBAR_GROUPS_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Plan-based feature gating — skipped for platform operators
  const { data: planLimitsData } = useGetPlanLimitsQuery(undefined, { skip: platformMode });
  const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: platformMode });
  const { data: orgProductsData } = useGetOrgProductsQuery(undefined, {
    skip: !isOrgAdminRole(user?.role) || platformMode,
  });
  const { data: myLicenses } = useGetMyProductLicensesQuery(undefined, { skip: platformMode });
  const orgProductAccess = parseTenantProducts(
    (orgProductsData?.products
      ? { products: orgProductsData.products }
      : tenant?.settings) as Record<string, unknown> | undefined,
  );
  const planOrgEnabled = orgProductAccess.pm?.enabled === true;
  const hasPlanLicense = (myLicenses?.launchableProductIds ?? []).includes('pm');
  const economicsEnabled = Boolean((tenant?.settings as any)?.economics?.enabled);
  const tenantSettings = (tenant?.settings ?? {}) as { logoUrl?: string; brandColor?: string };
  const tenantLogoUrl = tenantSettings.logoUrl;
  const tenantBrandColor = tenantSettings.brandColor;
  const planFeatures = planLimitsData?.limits?.features as Record<string, boolean> | undefined;

  useEffect(() => {
    if (tenantBrandColor) {
      document.documentElement.style.setProperty('--brand', tenantBrandColor);
    }
  }, [tenantBrandColor]);

  /** Maps requiredFeature key → plan name shown in lock tooltip */
  const FEATURE_UPGRADE_PLAN: Record<string, string> = {
    advancedReporting: 'CoTaskr Plan',
    integrations: 'CoTaskr Plan',
    webhooks: 'Enterprise',
    apiAccess: 'Enterprise',
    customRoles: 'Enterprise',
  };

  const groups: NavGroup[] = [
    {
      label: 'HOME',
      items: [
        {
          name: 'Dashboard',
          path: '/dashboard',
          tourId: 'nav-dashboard',
          iconContent: (
            <>
              <rect x="2" y="2" width="6" height="6" rx="1.5" />
              <rect x="10" y="2" width="6" height="6" rx="1.5" />
              <rect x="2" y="10" width="6" height="6" rx="1.5" />
              <rect x="10" y="10" width="6" height="6" rx="1.5" />
            </>
          ),
        },
        {
          name: 'Projects',
          path: '/projects',
          tourId: 'nav-projects',
          iconContent: <path d="M2 5h14M2 9h14M2 13h8" />,
        },
        {
          name: 'Handbook',
          path: '/handbook',
          tourId: 'nav-handbook',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <path d="M4 2h10a1 1 0 011 1v13a1 1 0 01-1 1H4a1 1 0 01-1-1V3a1 1 0 011-1z" />
              <path d="M7 6h4M7 9h6M7 12h3" />
            </>
          ),
        },
      ],
    },
    {
      label: 'WORK',
      items: [
        {
          name: 'Board',
          path: '/board',
          tourId: 'nav-board',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <rect x="2" y="3" width="4" height="12" rx="1.5" />
              <rect x="7" y="3" width="4" height="8" rx="1.5" />
              <rect x="12" y="3" width="4" height="10" rx="1.5" />
            </>
          ),
        },
        {
          name: 'List',
          path: '/list',
          tourId: 'nav-list',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <path d="M5 5h10M5 9h10M5 13h6" />
              <circle cx="2.5" cy="5" r="1" fill="currentColor" stroke="none" />
              <circle cx="2.5" cy="9" r="1" fill="currentColor" stroke="none" />
              <circle cx="2.5" cy="13" r="1" fill="currentColor" stroke="none" />
            </>
          ),
        },
        {
          name: 'Backlog',
          path: '/backlog',
          tourId: 'nav-backlog',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <rect x="2" y="2" width="14" height="3" rx="1" />
              <rect x="2" y="7" width="14" height="3" rx="1" />
              <rect x="2" y="12" width="14" height="3" rx="1" />
            </>
          ),
        },
        {
          name: 'Sprints',
          path: '/sprints',
          tourId: 'nav-sprints',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <circle cx="9" cy="9" r="7" />
              <path d="M9 5v4l3 2" />
            </>
          ),
        },
        {
          name: 'Milestones',
          path: '/milestones',
          tourId: 'nav-milestones',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <circle cx="4" cy="14" r="1.5" />
              <circle cx="14" cy="5" r="1.5" />
              <path d="M5.5 13l7-6.5M4 3v7M2 5h4" />
            </>
          ),
        },
        {
          name: 'Schedule',
          path: '/gantt',
          tourId: 'nav-gantt',
          requiredFeature: 'advancedReporting',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <rect x="4" y="3" width="10" height="2.5" rx="1" />
              <rect x="2" y="7.5" width="8" height="2.5" rx="1" />
              <rect x="6" y="12" width="10" height="2.5" rx="1" />
            </>
          ),
        },
      ],
    },
    {
      label: 'TIME',
      items: [
        {
          name: 'Timesheet',
          path: '/time-tracking',
          tourId: 'nav-time-tracking',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <circle cx="9" cy="9" r="7" />
              <path d="M9 5v4l2.5 2.5" />
            </>
          ),
        },
        {
          name: 'Calendar',
          path: '/calendar',
          tourId: 'nav-calendar',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <rect x="2" y="3" width="14" height="13" rx="1.5" />
              <path d="M2 7h14M6 1v4M12 1v4" />
            </>
          ),
        },
        {
          name: 'Workload',
          path: '/workload',
          tourId: 'nav-workload',
          requiredFeature: 'advancedReporting',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER],
          iconContent: (
            <>
              <rect x="2" y="12" width="3" height="4" rx="0.5" />
              <rect x="6.5" y="8" width="3" height="8" rx="0.5" />
              <rect x="11" y="4" width="3" height="12" rx="0.5" />
            </>
          ),
        },
      ],
    },
    {
      label: 'AI',
      visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
      items: [
        {
          name: 'AI Hub',
          path: '/ai',
          tourId: 'nav-ai-hub',
          end: true,
          iconContent: (
            <>
              <rect x="4" y="4" width="10" height="10" rx="2" />
              <path d="M7 1v3M11 1v3M7 14v3M11 14v3M1 7h3M1 11h3M14 7h3M14 11h3" />
              <circle cx="9" cy="9" r="2" fill="currentColor" stroke="none" />
            </>
          ),
        },
        {
          name: 'AI Studio',
          path: '/ai-studio',
          tourId: 'nav-ai-studio',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <circle cx="9" cy="9" r="3" />
              <path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.6 3.6l1.4 1.4M13 13l1.4 1.4M3.6 14.4l1.4-1.4M13 5l1.4-1.4" />
            </>
          ),
        },
        {
          name: 'AI Assistant',
          path: '/ai-assistant',
          tourId: 'nav-ai-assistant',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <path d="M3 4h12v8H7l-3 3V4z" />
              <path d="M7 8h4M7 11h2" />
            </>
          ),
        },
        {
          name: 'Whiteboard',
          path: '/ai-whiteboard',
          tourId: 'nav-ai-whiteboard',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: (
            <>
              <rect x="2" y="3" width="14" height="11" rx="1.5" />
              <path d="M5 10l2.5-3 2.5 2 2.5-4" />
            </>
          ),
        },
      ],
    },
    {
      label: 'INSIGHTS',
      items: [
        {
          name: 'Reports',
          path: '/reports',
          tourId: 'nav-reports',
          requiredFeature: 'advancedReporting',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.MEMBER],
          iconContent: <path d="M2 14L5.5 9l3 2.5L14 4" />,
        },
        {
          name: 'Stakeholder',
          path: '/stakeholder',
          tourId: 'nav-stakeholder',
          requiredFeature: 'advancedReporting',
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER, UserRole.STAKEHOLDER],
          iconContent: (
            <>
              <circle cx="9" cy="6" r="3.5" />
              <path d="M2 17c0-3.5 3-6 7-6s7 2.5 7 6" />
            </>
          ),
        },
        {
          name: 'Economics',
          path: '/economics',
          tourId: 'nav-economics',
          requiresEconomics: true,
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER],
          iconContent: (
            <>
              <path d="M2 13l4-4 3 2.5 4-5 3 3" />
              <path d="M2 16h14" />
            </>
          ),
        },
        {
          name: 'Cost',
          path: '/cost',
          tourId: 'nav-cost',
          requiresEconomics: true,
          visibleTo: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.MANAGER],
          iconContent: (
            <>
              <circle cx="9" cy="9" r="7" />
              <path d="M9 5v1m0 6v1m-2-4h3a1 1 0 010 2H8a1 1 0 000 2h3" />
            </>
          ),
        },
      ],
    },
  ];

  // Plan delivery — product configuration only (org admin uses org console for members/billing/SSO).
  groups.push({
    label: 'ADMIN',
    visibleTo: [UserRole.ADMIN],
    items: [
      {
        name: 'Plan Settings',
        path: '/admin/org-settings',
        tourId: 'nav-org-settings',
        iconContent: (
          <>
            <circle cx="9" cy="9" r="3" />
            <path d="M9 1v2M9 15v2M1 9h2M15 9h2M3.6 3.6l1.4 1.4M13 13l1.4 1.4M3.6 14.4l1.4-1.4M13 5l1.4-1.4" />
          </>
        ),
      },
    ],
  });

  // Filter groups and items based on user role
  const filteredGroups = useMemo(() => {
    const userRole = user?.role as UserRole | undefined;
    return groups
      .filter((group) => {
        if (!group.visibleTo) return true;
        return userRole && group.visibleTo.includes(userRole);
      })
      .map((group) => ({
        ...group,
        items: group.items
          .filter((item) => {
            if (item.requiresEconomics && !economicsEnabled) return false;
            if (!item.visibleTo) return true;
            return userRole && item.visibleTo.includes(userRole);
          })
          .map((item) => ({
            ...item,
            isLocked: item.requiredFeature
              ? planFeatures !== undefined && !planFeatures[item.requiredFeature]
              : false,
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [user?.role, planFeatures, economicsEnabled]);

  return (
    <nav
      data-tour="sidebar"
      className={`relative bg-sidebar flex flex-col shrink-0 overflow-y-auto overflow-x-hidden scrollbar-thin z-50
        max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:shadow-xl
        max-lg:transition-transform max-lg:duration-200
        ${mobileOpen ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'}
        ${isResizing ? '' : 'transition-[width] duration-[250ms] ease-in-out'}`}
      style={{ width: sidebarWidthPx }}
    >
      {/* Brand — org logo, or full CoTaskr wordmark when expanded and no logo */}
      <div
        className="relative z-20 h-[52px] flex items-center border-b shrink-0 overflow-visible"
        style={{ borderColor: 'var(--nav-border)' }}
      >
        {isCollapsed ? (
          <div className="flex h-full w-full items-center justify-between gap-0 pe-0 ps-0">
            <SidebarBrandMark
              logoUrl={tenantLogoUrl}
              orgName={tenant?.name}
              userRole={user?.role as UserRole | undefined}
              isCollapsed
            />
            <div className="flex shrink-0 items-center pe-0">
              <SidebarToggle collapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            </div>
          </div>
        ) : (
          <div className="flex h-full w-full min-w-0 items-center">
            <SidebarBrandMark
              logoUrl={tenantLogoUrl}
              orgName={tenant?.name}
              userRole={user?.role as UserRole | undefined}
              isCollapsed={false}
            />
            <div className="flex h-full shrink-0 items-center pe-0">
              <SidebarToggle collapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
            </div>
          </div>
        )}
      </div>

      {/* Project Selector — hidden in platform / org-console shells */}
      {!platformMode && !orgConsoleMode && <ProjectSelector isCollapsed={isCollapsed} />}

      {/* Navigation */}
      <div className="flex-1 py-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
        {platformMode ? (
          <div className="px-2 pb-0.5 pt-1">
            {!isCollapsed && (
              <div className="px-2.5 py-1">
                <span
                  className="text-[10px] font-semibold tracking-[.1em]"
                  style={{ color: 'var(--nav-label)' }}
                >
                  {t('nav.groups.PLATFORM', 'PLATFORM')}
                </span>
              </div>
            )}
            {PLATFORM_NAV.map((item) => {
              const to = item.tab === 'roles' ? '/roles' : platformAdminPath(item.tab);
              const isActive =
                item.tab === 'roles'
                  ? location.pathname.startsWith('/roles')
                  : location.pathname === '/admin' && activePlatformTab === item.tab;
              return (
                <NavLink
                  key={item.tab}
                  to={to}
                  end={item.tab === 'organizations'}
                  className={`relative flex items-center gap-2 py-[5px] rounded-[var(--radius-sm)] text-[11.5px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap overflow-hidden ${
                    isCollapsed ? 'justify-center px-1.5' : 'pl-[1.05rem] pr-2.5'
                  } ${isActive ? 'nav-item-active' : 'nav-item'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <I>{item.iconContent}</I>
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
            {!isCollapsed && (
              <div className="px-2.5 py-2 mt-2">
                <span
                  className="text-[10px] font-semibold tracking-[.1em]"
                  style={{ color: 'var(--nav-label)' }}
                >
                  {t('nav.groups.MY_ORG', 'MY ORG')}
                </span>
              </div>
            )}
            {isCollapsed && (
              <div className="mx-1 my-2 border-t" style={{ borderColor: 'var(--nav-border)' }} />
            )}
            {PLATFORM_MY_ORG.map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2 py-[5px] rounded-[var(--radius-sm)] text-[11.5px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap overflow-hidden ${
                    isCollapsed ? 'justify-center px-1.5' : 'pl-[1.05rem] pr-2.5'
                  } ${isActive ? 'nav-item-active' : 'nav-item'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <I>{item.iconContent}</I>
                  {!isCollapsed && <span className="flex-1">{item.label}</span>}
                </NavLink>
              );
            })}
            {!isCollapsed && (
              <div className="px-2.5 py-2 mt-2">
                <span
                  className="text-[10px] font-semibold tracking-[.1em]"
                  style={{ color: 'var(--nav-label)' }}
                >
                  {t('nav.groups.TOOLS', 'PERSONAL')}
                </span>
              </div>
            )}
            {isCollapsed && (
              <div className="mx-1 my-2 border-t" style={{ borderColor: 'var(--nav-border)' }} />
            )}
            {PLATFORM_PERSONAL.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2 py-[5px] rounded-[var(--radius-sm)] text-[11.5px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap overflow-hidden ${
                    isCollapsed ? 'justify-center px-1.5' : 'pl-[1.05rem] pr-2.5'
                  } ${isActive ? 'nav-item-active' : 'nav-item'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <I>{item.iconContent}</I>
                  {!isCollapsed && <span className="flex-1">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ) : orgConsoleMode ? (
          <div className="px-2 pb-0.5 pt-1">
            {!isCollapsed && (
              <div className="px-2.5 py-1">
                <span
                  className="text-[10px] font-semibold tracking-[.1em]"
                  style={{ color: 'var(--nav-label)' }}
                >
                  {t('nav.groups.ORG', 'ORGANIZATION')}
                </span>
              </div>
            )}
            {ORG_CONSOLE_NAV.filter(
              (item) => item.path !== '/projects' || (planOrgEnabled && hasPlanLicense),
            ).map((item) => {
              const pathOnly = item.path.split('?')[0];
              const isActive =
                location.pathname === pathOnly ||
                (pathOnly !== '/org/products' && location.pathname.startsWith(`${pathOnly}/`));
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`relative flex items-center gap-2 py-[5px] rounded-[var(--radius-sm)] text-[11.5px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap overflow-hidden ${
                    isCollapsed ? 'justify-center px-1.5' : 'pl-[1.05rem] pr-2.5'
                  } ${isActive ? 'nav-item-active' : 'nav-item'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <I>{item.iconContent}</I>
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ) : (
        <>
        {/* Admin back link removed — use the product switcher in the header (⊞ icon) to navigate back to the admin portal */}
        {filteredGroups.map((group) => {
          const isGroupCollapsed = !isCollapsed && collapsedGroups.has(group.label);
          return (
            <div key={group.label} className="px-2 pb-0.5 pt-1">
              {!isCollapsed ? (
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="w-full flex items-center justify-between px-2.5 py-1 text-left group"
                >
                  <span className="text-[10px] font-semibold tracking-[.1em] whitespace-nowrap group-hover:text-text-secondary transition-colors" style={{ color: 'var(--nav-label)' }}>
                    {GROUP_LABEL_KEYS[group.label] ? t(GROUP_LABEL_KEYS[group.label]) : group.label}
                  </span>
                  <svg
                    className={`w-3 h-3 shrink-0 stroke-current fill-none transition-transform duration-200 ${isGroupCollapsed ? '-rotate-90' : ''}`}
                    style={{ color: 'var(--nav-label)' }}
                    strokeWidth={2}
                    strokeLinecap="round"
                    viewBox="0 0 12 12"
                  >
                    <path d="M2 4l4 4 4-4" />
                  </svg>
                </button>
              ) : (
                /* Collapsed sidebar: show divider instead of label */
                <div className="mx-1 mb-1 border-t" style={{ borderColor: 'var(--nav-border)' }} />
              )}
              {!isGroupCollapsed && group.items.map((item) => {
                const navLabel = NAV_LABEL_KEYS[item.path] ? t(NAV_LABEL_KEYS[item.path]) : item.name;
                const upgradePlan = item.requiredFeature
                  ? FEATURE_UPGRADE_PLAN[item.requiredFeature] || 'Enterprise'
                  : null;
                const lockTitle =
                  item.isLocked && upgradePlan
                    ? `${navLabel} requires ${upgradePlan} — open to upgrade or view plans`
                    : undefined;
                return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.end}
                  data-tour={item.tourId}
                  className={({ isActive }) =>
                    `relative flex items-center gap-2 py-[5px] rounded-[var(--radius-sm)] text-[11.5px] font-medium cursor-pointer transition-all duration-150 whitespace-nowrap overflow-hidden ${
                      isCollapsed ? 'justify-center px-1.5' : 'pl-[1.05rem] pr-2.5'
                    } ${item.isLocked ? 'opacity-75' : ''} ${isActive ? 'nav-item-active' : 'nav-item'}`
                  }
                  title={isCollapsed ? navLabel : lockTitle || navLabel}
                >
                  <I>{item.iconContent}</I>
                  {!isCollapsed && (
                    <>
                      <span className="flex-1">{navLabel}</span>
                      {item.isLocked && (
                        <FiLock className="w-3 h-3 shrink-0 opacity-60" aria-hidden />
                      )}
                      {item.badge && (
                        <span
                          className={`ml-auto text-[10px] font-mono px-1.5 py-px rounded-[10px] transition-opacity ${
                            item.badgeClass ||
                            'bg-border text-text-muted'
                          }`}
                        >
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              );})}
            </div>
          );
        })}
        </>
        )}
      </div>

      {/* Powered by AiLenc · CoTaskr — bottom of sidebar */}
      <div
        className="shrink-0 border-t px-2 py-2"
        style={{ borderColor: 'var(--nav-border)' }}
      >
        <div
          className={`flex items-center gap-1.5 ${isCollapsed ? 'justify-center' : 'px-1.5'}`}
          title="Powered by AiLenc · CoTaskr"
        >
          <CoTaskrLogo size="sidebar" theme="brand" animated={false} iconOnly />
          {!isCollapsed && (
            <span className="text-[10px] text-text-muted leading-tight truncate">
              Powered by AiLenc · CoTaskr
            </span>
          )}
        </div>
      </div>

      {/* Drag to resize when expanded */}
      {!isCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize sidebar"
          onMouseDown={startResize}
          onDoubleClick={() => onWidthChange(DEFAULT_SIDEBAR_WIDTH)}
          className={`absolute top-0 bottom-0 z-30 w-1.5 cursor-col-resize touch-none transition-colors end-0 ${
            isResizing ? 'bg-brand/40' : 'hover:bg-brand/25'
          }`}
        />
      )}
    </nav>
  );
}
